/**
 * Regional formats — how a workshop writes dates and numbers.
 *
 * Mirror of `shared/py/bolloprint_shared/format_locales.py` (the list and the
 * defaults; `test_format_locales.py` fails if they drift). The screens format
 * with `Intl` in the tag; the backend reads a table generated from the same
 * CLDR data, so an email says what the screen says.
 *
 * A workshop has ONE format (owner's decision, 2026-10-21): its own choice in
 * Settings → General, else its country's. A person never changes it; their
 * language only decides the WORDS (a month written out).
 */

/** Every format a workshop can pick. */
export const FORMAT_LOCALES: readonly string[] = [
    'es-ES', 'pt-PT', 'fr-FR', 'it-IT', 'de-DE', 'nl-NL', 'nl-BE', 'fr-BE', 'de-BE', 'en-IE', 'de-AT',
    'pl-PL', 'cs-CZ', 'sk-SK', 'hu-HU', 'ro-RO', 'bg-BG', 'el-GR', 'hr-HR', 'sl-SI', 'sv-SE', 'da-DK',
    'fi-FI', 'sv-FI', 'et-EE', 'lv-LV', 'lt-LT', 'fr-LU', 'de-LU', 'en-MT', 'el-CY', 'en-GB', 'de-CH',
    'fr-CH', 'it-CH', 'nb-NO', 'is-IS', 'de-LI', 'ca-AD', 'fr-MC', 'it-SM', 'it-VA', 'en-GI', 'en-JE',
    'en-GG', 'en-IM', 'fo-FO', 'sq-AL', 'bs-BA', 'mk-MK', 'sr-Latn-RS', 'sr-Latn-ME', 'sq-XK', 'uk-UA',
    'ro-MD', 'tr-TR', 'ka-GE', 'hy-AM', 'az-AZ', 'kk-KZ', 'en-US', 'en-CA', 'fr-CA', 'es-MX', 'es-AR',
    'pt-BR', 'es-CO', 'es-CL', 'es-PE', 'es-UY', 'es-EC', 'es-VE', 'es-DO',
];

/** The format of a workshop with no country, by its language. */
export const LANGUAGE_FORMAT_DEFAULT: Readonly<Record<string, string>> = {
    es: 'es-ES', en: 'en-GB', fr: 'fr-FR', it: 'it-IT', de: 'de-DE', pt: 'pt-PT',
    pl: 'pl-PL', nl: 'nl-NL', cs: 'cs-CZ',
};

const BY_LOWER = new Map(FORMAT_LOCALES.map((c) => [c.toLowerCase(), c]));

/** The canonical tag if `code` names a known format (any case), else `null`. */
export function normalizeFormatLocale(code: string | null | undefined): string | null {
    return (code && BY_LOWER.get(code.trim().toLowerCase())) || null;
}

/** The BCP-47 tag of a UI language (`de` → `de-DE`); unknown → `en-GB`. */
export function languageFormatLocale(language: string | null | undefined): string {
    return LANGUAGE_FORMAT_DEFAULT[(language || 'en').trim().toLowerCase().slice(0, 2)] || 'en-GB';
}

/** The region of a tag (`sr-Latn-RS` → `RS`). */
export function formatLocaleRegion(code: string): string {
    const parts = code.split('-');
    return parts[parts.length - 1] ?? code;
}

/** `21.10.2026 · 1.234,56` — how `code` writes a date and a number, for pickers. */
export function formatLocaleExample(code: string): string {
    const day = new Date(Date.UTC(2026, 9, 21));
    const date = new Intl.DateTimeFormat(code, { timeZone: 'UTC', day: '2-digit', month: '2-digit', year: 'numeric' }).format(day);
    const num = new Intl.NumberFormat(code, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(1234.56);
    return `${date} · ${num}`;
}

/**
 * `Austria (alemán)` in `uiLocale` — the country and the language of a format.
 * Falls back to the tag when the engine has no `Intl.DisplayNames`.
 */
export function formatLocaleName(code: string, uiLocale: string): string {
    try {
        const regions = new Intl.DisplayNames([uiLocale], { type: 'region' });
        const languages = new Intl.DisplayNames([uiLocale], { type: 'language' });
        const lang = code.split('-')[0] ?? code;
        return `${regions.of(formatLocaleRegion(code)) ?? code} (${languages.of(lang) ?? lang})`;
    } catch {
        return code;
    }
}
